import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletBonusReport } from "../../Redux/Reducers/WalletBonusReportReducer";
import { formatUTCDateForDataGrid } from "../../utils/dateUtils";
import axios from "axios";

const WalletBonusReport = () => {
  const dispatch = useDispatch();
  const { data: rowsData = [], paginationDetail = {}, status = "idle" } =
    useSelector((state) => state?.WalletBonusReportReducer || {});

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("userName");
  const [appliedSearchType, setAppliedSearchType] = useState("userName");
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    dispatch(
      fetchWalletBonusReport({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: appliedSearchQuery,
        searchType: appliedSearchType,
      })
    );
  }, [
    dispatch,
    paginationModel.page,
    paginationModel.pageSize,
    appliedSearchQuery,
    appliedSearchType,
  ]);

  const handleApplyFilters = () => {
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSearchType(searchType);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSearchType("userName");
    setAppliedSearchType("userName");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/walletBonusReportExport`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      
      const exportData = response.data?.data || [];
      if (exportData.length === 0) {
        alert("No data available to export");
        setIsExporting(false);
        return;
      }
      
      const formattedData = exportData.map((item) => ({
        "User ID": item.userId || "-",
        "User Name": item.userName || "-",
        "Campaign ID": item.campaignId || "-",
        "Campaign Name": item.campaignName || "-",
        "Transaction Type": item.transactionType || "-",
        "Recharge Category": item?.rechargeCategory || "-",
        "Recharge Amount": item.rechargeAmount || 0,
        "Recharge Amount Used": item.rechargeAmountUsed || 0,
        "Recharge Balance": item.rechargeBalance || 0,
        "Bonus Amount": item.bonusAmount || 0,
        "Bonus Used": item.bonusUsed || 0,
        "Bonus Balance": item.bonusBalance || 0,
        "Expiry Status": item.expiryStatus || "-",
        "Bonus Expiry Date": item.bonusExpiryDate ? new Date(item.bonusExpiryDate).toLocaleString() : "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "WalletBonusReport");
      XLSX.writeFile(workbook, "Wallet_Bonus_Report.xlsx");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { field: "userId", headerName: "User ID", width: 220, renderCell: (params) => params.value || "-" },
    { field: "userName", headerName: "User Name", width: 150, renderCell: (params) => params.value || "-" },
    { field: "campaignId", headerName: "Campaign ID", width: 220, renderCell: (params) => params.value || "-" },
    { field: "campaignName", headerName: "Campaign Name", width: 150, renderCell: (params) => params.value || "-" },
    { field: "transactionType", headerName: "Transaction Type", width: 150, renderCell: (params) => params.value || "-" },
    { field: "rechargeCategory", headerName: "Recharge Category", width: 150, renderCell: (params) => params.value || "-" },
    { field: "rechargeAmount", headerName: "Recharge Amount", width: 140 },
    { field: "rechargeAmountUsed", headerName: "Recharge Amount Used", width: 180 },
    { field: "rechargeBalance", headerName: "Recharge Balance", width: 150 },
    { field: "bonusAmount", headerName: "Bonus Amount", width: 120 },
    { field: "bonusUsed", headerName: "Bonus Used", width: 100 },
    { field: "bonusBalance", headerName: "Bonus Balance", width: 120 },
    { field: "expiryStatus", headerName: "Expiry Status", width: 120, renderCell: (params) => params.value || "-" },
    {
      field: "bonusExpiryDate",
      headerName: "Bonus Expiry Date",
      width: 200,
      valueFormatter: formatUTCDateForDataGrid,
    },
  ];


  return (
    <div className="container-xxl">
      <PageHeader1
        pagetitle="Wallet Bonus Report"
        modalbutton={() => (
          <div className="col-auto d-flex w-sm-100">
            <Button
              variant="contained"
              style={{ backgroundColor: "#22744B", color: "white" }}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? <CircularProgress size={24} color="inherit" /> : "Export CSV"}
            </Button>
          </div>
        )}
      />

      <div className="row g-3 mb-3 custom-filter-container">
        <div className="col-md-3">
          <FormControl fullWidth size="small">
            <InputLabel id="search-type-label">Search Key</InputLabel>
            <Select
              labelId="search-type-label"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              label="Search Key"
            >
              <MenuItem value="userName">Username</MenuItem>
              {/* <MenuItem value="mobile">Mobile Number</MenuItem> */}
            </Select>
          </FormControl>
        </div>

        <div className="col-md-3">
          <TextField
            fullWidth
            label="Search value..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleApplyFilters();
              }
            }}
          />
        </div>

        <div className="col-md-3 d-flex align-items-center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyFilters}
            className="me-2"
          >
            Filter
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleResetFilters}>
            Reset
          </Button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={rowsData}
              columns={columns}
              loading={status === "loading"}
              paginationMode="server"
              hideFooterPagination
              disableRowSelectionOnClick
            />
          </Box>
          <TablePagination
            component="div"
            count={paginationDetail?.totalRecords || 0}
            page={paginationModel.page}
            onPageChange={(event, newPage) =>
              setPaginationModel((prev) => ({ ...prev, page: newPage }))
            }
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={(event) =>
              setPaginationModel((prev) => ({
                ...prev,
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              }))
            }
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      </div>
    </div>
  );
};

export default WalletBonusReport;
